import {lazy, Suspense, useRef, memo, useCallback} from 'react';
import {cn} from '@/utils';

const StarRatings = lazy(() => import('react-star-ratings'));

const TS_WIDGET_INNER_MARGIN = 20; // px — shadow DOM .wrapper has margin: 20px (not overridable)

const Ratings = memo(({className, starDimension, ratings, mobile = false}) => {
    const {type, data, snippet} = ratings || {};
    const containerRef = useRef(null);
    const widgetRef = useRef(null);
    const cleanupRef = useRef(null);
    const enableTrustPilot = window.Trustpilot !== undefined;

    const alignWidget = useCallback((wrapperWidth, wrapperNode) => {
      const widget = widgetRef.current;
      if (!widget) return;
      // Briefly switch to inline-block to read the widget's natural content width.
      // This is a synchronous read — no paint occurs between the two style changes.
      widget.style.display = 'inline-block';
      const naturalWidth = widget.offsetWidth;
      widget.style.display = '';
      if (naturalWidth === 0) return; // widget CDN content not loaded yet
      // justify-content: center puts the content at (wrapperWidth - naturalWidth) / 2 from
      // the inner margin edge. Add the inner margin to get the offset from the host's left edge.
      const offset = (wrapperWidth - naturalWidth) / 2 + TS_WIDGET_INNER_MARGIN;
      widget.style.transform = `translateX(${-offset}px)`;
      // Reveal the wrapper now that the transform is correct.
      if (wrapperNode) wrapperNode.style.opacity = '1';
    }, []);

    return (
      <Choose>
        <When condition={type === 'rating-widget-ts'}>
          {data.widgetId && (
            <div
              ref={(node) => {
                if (cleanupRef.current) {
                  cleanupRef.current();
                  cleanupRef.current = null;
                }
                if (!node) return;

                // Watch wrapper for viewport/container resize once widget is revealed.
                const resizeObserver = new ResizeObserver((entries) => {
                  window.dispatchEvent(new Event('resize'));
                  alignWidget(entries[0].contentRect.width, node);
                });
                resizeObserver.observe(node);

                // The widget has a fixed height from its shadow DOM CSS so ResizeObserver
                // won't fire again after load. It exposes no lifecycle events or attribute
                // changes either. Polling is therefore the only reliable detection method.
                let pollTimer = null;
                let pollAttempts = 0;
                const poll = () => {
                  if (!widgetRef.current) return;
                  alignWidget(node.offsetWidth, node);
                  if (node.style.opacity !== '1' && pollAttempts++ < 50) {
                    pollTimer = setTimeout(poll, 200);
                  }
                };
                pollTimer = setTimeout(poll, 200);

                cleanupRef.current = () => {
                  resizeObserver.disconnect();
                  clearTimeout(pollTimer);
                };
              }}
              className={cn(
                className,
                'ts-widget-wrapper max-h-5xl -mt-sm',
                mobile ? 'mb-sm' : '',
              )}>
              <etrusted-widget
                ref={widgetRef}
                data-etrusted-widget-id={data.widgetId}
              ></etrusted-widget>
            </div>
          )}
        </When>
        <When condition={type === 'rating-stars-ts'}>
          <div
            ref={containerRef}
            className={cn(
              className,
              'relative flex flex-wrap gap-x-sm items-center'
            )}
          >
            {/* StarRatings is lazy loaded; without a Suspense boundary its first
                render during a synchronous update crashes the whole React root.
                The fallback reserves the stars' box (spacing is 0) against shifts. */}
            <Suspense
              fallback={
                <span
                  style={{
                    width: `calc(${starDimension ? starDimension : '24px'} * ${data.max})`,
                    height: starDimension ? starDimension : '24px',
                  }}
                />
              }
            >
              <StarRatings
                rating={data.current}
                starRatedColor="#ffdc0f"
                numberOfStars={data.max}
                name="rating"
                starDimension={starDimension ? starDimension : '24px'}
                starSpacing="0px"
              />
            </Suspense>
            <div className="flex gap-3xs items-center">
            <span className="font-semibold text-xs desktop:text-sm">
              {data.current} / {data.max} ({data.reviewCount}{' '}
              {Drupal.t('Ratings')})
            </span>
              <ft-tooltip behavior="click" bg-color="accent">
                <ft-icon name="info" slot="trigger"></ft-icon>
                <a
                  className="leading-5 hover:underline hover:underline-offset-4"
                  style={{color: 'rgba(34, 64, 177, 1)'}}
                  href={data.url + '#legal'} target="_blank"
                >
                  {Drupal.t(
                    'About the authenticity of the Trusted Shops ratings.'
                  )}
                </a>
              </ft-tooltip>
            </div>
          </div>
        </When>
        <When condition={type === 'trustpilot' && enableTrustPilot}>
          <div
            ref={(e) => window.Trustpilot.loadFromElement(e, true)}
            className={cn(className, 'trustpilot-widget -ml-sm')}
            data-locale={data.locale}
            data-template-id="5419b732fbfb950b10de65e5"
            data-businessunit-id={data.businessUnitId}
            data-style-height="24px"
            data-style-width="auto"
            data-theme="light"
          >
            &nbsp;
          </div>
        </When>
        <When condition={type === 'snippet' && snippet}>
          <div {...{className}} dangerouslySetInnerHTML={{__html: snippet}}></div>
        </When>
      </Choose>
    );

  }, (prevProps, nextProps) => {
    return true;
  }
);

export default Ratings;
